import React from 'react';
import {
  makeStyles,
  Typography,
  IconButton,
  Grid,
  Theme,
  Box,
  CircularProgress,
} from '@material-ui/core';

import colors from '../../../theme/colors';

import { shortenFilename } from './shortenFilename';
import { check, pdf, trash } from '../../../svg';

type Props = {
  deleteButtonId?: string;
  displayValue: string;
  requestStatus: string;
  onDelete?: () => void;
  disabled?: boolean;
};

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    width: '100%',
    paddingTop: '0.8rem',
    paddingBottom: '0.8rem',
    borderRadius: 10,
    backgroundColor: colors.cloud,
    position: 'relative',
  },
  pdfIcon: {
    marginRight: '1rem',
  },
  leftAnchor: {
    display: 'inline-block',
    width: 'calc(100% - 24px)',
  },
  fullLeftAnchor: {
    display: 'inline-block',
    width: '100%',
  },
  trashIcon: {
    width: 24,
    height: 24,
    position: 'absolute',
    right: theme.spacing(2),
  },
  loadingIcon: {
    position: 'absolute',
    right: theme.spacing(2),
  },
  successUpload: {
    animation: `$successUpload 1000ms ${theme.transitions.easing.easeInOut}`,
    animationFillMode: 'forwards',
  },
  displayEffect: {
    animation: `$displayEffect 1000ms ${theme.transitions.easing.sharp}`,
    animationFillMode: 'forwards',
  },
  '@keyframes successUpload': {
    '0%': {
      width: 0,
    },
    '50%': {
      width: '100%',
      opacity: 1,
    },
    '75%': {
      opacity: 0,
    },
    '100%': {
      opacity: 0,
    },
  },
  '@keyframes displayEffect': {
    '0%': {
      opacity: 0,
      transform: 'scale(0)',
    },
    '85%': {
      opacity: 0,
      transform: 'scale(0)',
    },
    '100%': {
      transform: 'scale(1)',
      opacity: 1,
    },
  },
}));

const PdfPreview = ({
  displayValue,
  onDelete = null,
  deleteButtonId = '',
  disabled = false,
  requestStatus,
}: Props): JSX.Element => {
  const classes = useStyles();
  const isRequesting = requestStatus === 'requesting';
  const success = requestStatus === 'success';

  return (
    <Grid
      container
      alignItems="center"
      className={classes.container}
      wrap="nowrap"
    >
      <div
        data-testid="left-anchor"
        className={
          onDelete !== null ? classes.leftAnchor : classes.fullLeftAnchor
        }
      >
        <Typography
          data-testid="typography-display"
          variant="h2"
          align="center"
        >
          <span className={classes.pdfIcon}>
            <img alt="pdf" src={pdf} />
          </span>
          {shortenFilename(displayValue)}
        </Typography>
      </div>
      {isRequesting && (
        <Box className={classes.loadingIcon} data-testid="loading-icon">
          <CircularProgress size={24} />
        </Box>
      )}
      {success && (
        <Box className={`${classes.loadingIcon}`} data-testid="success-icon">
          <img
            src={check}
            alt="success-upload"
            className={classes.successUpload}
          />
        </Box>
      )}
      {onDelete !== null && !isRequesting && (
        <IconButton
          id={deleteButtonId}
          data-testid="delete-button"
          className={`${classes.trashIcon} ${classes.displayEffect}`}
          onClick={onDelete}
          disabled={disabled}
        >
          <img alt="trash" src={trash} />
        </IconButton>
      )}
    </Grid>
  );
};

export default PdfPreview;
